/**
 * Backend inbox v2 API — typed TanStack Query hooks against the
 * `/api/v1/inbox/*` surface (Phase 2 Task 2.1, commit 3e81a7d).
 *
 * Replaces the persona fixture in `components/screens/inbox.tsx`
 * (Phase 3 Task 3.2, 2026-05-17). Backend handlers:
 *   - GET    /v1/inbox/threads?trustId=&limit=&cursor=
 *   - GET    /v1/inbox/threads/{threadId}?trustId=
 *   - POST   /v1/inbox/threads
 *   - GET    /v1/inbox/threads/{threadId}/messages?trustId=&limit=&cursor=
 *   - POST   /v1/inbox/threads/{threadId}/messages?trustId=
 *   - POST   /v1/inbox/threads/{threadId}/read?trustId=
 *
 * Response shapes mirror the handler bodies — see the corresponding
 * `backend/src/handlers/inbox-*.ts` files for the source-of-truth.
 *
 * Distinct from the legacy `/v1/messages/*` GET surface, which the MOBILE
 * app's inbox reader still consumes (the web `messages.ts` hook set and the
 * legacy thread-reply endpoint were killed 2026-07-09 — wiring-map ruling,
 * zero web consumers). The inbox v2 surface is the canonical
 * multi-stakeholder communications hub going forward.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from './client';

// ── Shared types (mirror backend/src/lib/repositories/types.ts) ──────────────

export type InboxThreadStatus = 'open' | 'archived';
export type InboxParticipantRole =
  | 'trustee'
  | 'beneficiary'
  | 'admin'
  | 'external-counsel';

export interface InboxThreadParticipant {
  userId: string;
  displayName: string;
  role: InboxParticipantRole;
  addedAt: string;
  lastReadAt: string | null;
}

export interface InboxAttachment {
  blobName: string;
  contentType: string;
  sizeBytes: number;
  label: string;
}

/** Row returned by GET /v1/inbox/threads — summary projection. */
export interface InboxThreadSummary {
  id: string;
  trustId: string;
  subject: string;
  status: InboxThreadStatus;
  createdAt: string;
  createdByUserId: string;
  lastMessageAt: string;
  messageCount: number;
  attachmentsCount: number;
  participantCount: number;
  unreadForYou: boolean;
}

export interface InboxThreadsListResponse {
  threads: InboxThreadSummary[];
  nextCursor: string | null;
  correlationId: string;
}

/** Full thread document returned by GET /v1/inbox/threads/{id}. */
export interface InboxThreadDetail {
  id: string;
  tenantId: string;
  trustId: string;
  subject: string;
  status: InboxThreadStatus;
  createdAt: string;
  createdByUserId: string;
  lastMessageAt: string;
  participants: InboxThreadParticipant[];
  messageCount: number;
  attachmentsCount: number;
}

export interface InboxThreadGetResponse {
  thread: InboxThreadDetail;
  youAreParticipant: boolean;
  unreadForYou: boolean;
  correlationId: string;
}

export interface InboxMessage {
  id: string;
  tenantId: string;
  trustId: string;
  threadId: string;
  authorUserId: string;
  authorDisplayName: string;
  body: string;
  attachments: InboxAttachment[];
  createdAt: string;
  redactedAt?: string;
}

export interface InboxMessageListResponse {
  messages: InboxMessage[];
  nextCursor: string | null;
  correlationId: string;
}

export interface InboxMessagePostResponse {
  message: InboxMessage;
  correlationId: string;
}

export interface CreateInboxThreadParticipantInput {
  userId: string;
  displayName: string;
  role: InboxParticipantRole;
}

export interface CreateInboxThreadInput {
  trustId: string;
  subject: string;
  participants: CreateInboxThreadParticipantInput[];
  initialMessage?: { body: string };
}

export interface InboxThreadCreateResponse {
  thread: InboxThreadDetail;
  correlationId: string;
}

// ── Path builders ────────────────────────────────────────────────────────────

interface UseInboxThreadsOptions {
  trustId: string | undefined;
  limit?: number;
  cursor?: string | null;
  /** Defaults to !!trustId; explicit false defers the network call. */
  enabled?: boolean;
}

function buildThreadsListPath(opts: UseInboxThreadsOptions): string {
  const search = new URLSearchParams();
  if (opts.trustId) search.set('trustId', opts.trustId);
  if (opts.limit !== undefined) search.set('limit', String(opts.limit));
  if (opts.cursor) search.set('cursor', opts.cursor);
  const qs = search.toString();
  return qs ? `/api/v1/inbox/threads?${qs}` : '/api/v1/inbox/threads';
}

// ── Hooks ────────────────────────────────────────────────────────────────────

/** List threads visible to the caller on a single trust. */
export function useInboxThreads(opts: UseInboxThreadsOptions) {
  return useQuery<InboxThreadsListResponse>({
    queryKey: [
      'inbox',
      'threads',
      'list',
      {
        trustId: opts.trustId ?? null,
        limit: opts.limit ?? null,
        cursor: opts.cursor ?? null,
      },
    ],
    queryFn: ({ signal }) =>
      api<InboxThreadsListResponse>(buildThreadsListPath(opts), { signal }),
    enabled: opts.enabled ?? !!opts.trustId,
  });
}

/** Get full thread detail (participants + counters). */
export function useInboxThread(
  threadId: string | undefined,
  trustId: string | undefined,
) {
  return useQuery<InboxThreadGetResponse>({
    queryKey: ['inbox', 'thread', 'detail', threadId ?? null, trustId ?? null],
    queryFn: ({ signal }) => {
      const search = new URLSearchParams({ trustId: trustId! });
      return api<InboxThreadGetResponse>(
        `/api/v1/inbox/threads/${encodeURIComponent(threadId!)}?${search.toString()}`,
        { signal },
      );
    },
    enabled: !!threadId && !!trustId,
  });
}

/** List messages for a thread, oldest first. */
export function useInboxMessages(
  threadId: string | undefined,
  trustId: string | undefined,
  opts: { limit?: number; cursor?: string | null } = {},
) {
  return useQuery<InboxMessageListResponse>({
    queryKey: [
      'inbox',
      'messages',
      'list',
      threadId ?? null,
      trustId ?? null,
      { limit: opts.limit ?? null, cursor: opts.cursor ?? null },
    ],
    queryFn: ({ signal }) => {
      const search = new URLSearchParams({ trustId: trustId! });
      if (opts.limit !== undefined) search.set('limit', String(opts.limit));
      if (opts.cursor) search.set('cursor', opts.cursor);
      return api<InboxMessageListResponse>(
        `/api/v1/inbox/threads/${encodeURIComponent(threadId!)}/messages?${search.toString()}`,
        { signal },
      );
    },
    enabled: !!threadId && !!trustId,
  });
}

/** Open a new thread (optionally with an initial message). */
export function useCreateThread() {
  const queryClient = useQueryClient();
  return useMutation<InboxThreadCreateResponse, Error, CreateInboxThreadInput>({
    mutationFn: (body) =>
      api<InboxThreadCreateResponse>('/api/v1/inbox/threads', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

/** Post a new message into an existing thread. */
export function usePostMessage() {
  const queryClient = useQueryClient();
  return useMutation<
    InboxMessagePostResponse,
    Error,
    { threadId: string; trustId: string; body: string; attachments?: InboxAttachment[] }
  >({
    mutationFn: ({ threadId, trustId, body, attachments }) => {
      const search = new URLSearchParams({ trustId });
      return api<InboxMessagePostResponse>(
        `/api/v1/inbox/threads/${encodeURIComponent(threadId)}/messages?${search.toString()}`,
        {
          method: 'POST',
          body: attachments ? { body, attachments } : { body },
        },
      );
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['inbox', 'messages', 'list', variables.threadId],
      });
      void queryClient.invalidateQueries({ queryKey: ['inbox', 'threads', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox', 'thread', 'detail', variables.threadId] });
    },
  });
}

// ── Redact (REQ-INBOX-001 Item 9, POPIA) ────────────────────────────────────
//
// POST /v1/inbox/threads/{threadId}/messages/{messageId}/redact?trustId=…
// No request body. Sets `redactedAt` on the message — the row is retained for
// audit-trail / thread integrity, but this + every future list read swaps the
// body for a `[redacted]` placeholder (see backend applyRedaction in
// inbox-message-list.ts). IRREVERSIBLE from the product UI: there is no
// un-redact endpoint. admin / compliance / io / super_admin only; step-up
// gated server-side (mirrors legal-holds-create). Cross-tenant / missing
// message → 404.

export interface RedactMessageInput {
  threadId: string;
  messageId: string;
  trustId: string;
}

export interface InboxMessageRedactResponse {
  message: InboxMessage;
  correlationId: string;
}

export function useRedactMessage() {
  const queryClient = useQueryClient();
  return useMutation<InboxMessageRedactResponse, Error, RedactMessageInput>({
    mutationFn: ({ threadId, messageId, trustId }) => {
      const search = new URLSearchParams({ trustId });
      return api<InboxMessageRedactResponse>(
        `/api/v1/inbox/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(messageId)}/redact?${search.toString()}`,
        { method: 'POST' },
      );
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['inbox', 'messages', 'list', variables.threadId],
      });
    },
  });
}

// ── build-campaign 2026-07-09 · lane B · B6 + B7 ─────────────────────────────

/** The archive/participant-add responses return the full thread document —
 *  structurally InboxThreadDetail (the handlers serialise InboxThreadDoc). */
type InboxThreadDoc_ArchiveView = InboxThreadDetail;

/**
 * B6 — POST /v1/inbox/threads/{threadId}/archive?trustId=… (backend
 * `inbox-thread-archive.ts`). Sets the thread's status to 'archived'.
 * PARTICIPANT-gated (admin does NOT bypass — mirrors mark-read); no step-up;
 * idempotent (re-archiving returns the thread unchanged). There is NO
 * un-archive endpoint — archived threads stay readable but can't be reopened
 * from the product UI, so callers confirm before firing.
 */
export function useArchiveThread() {
  const queryClient = useQueryClient();
  return useMutation<
    { thread: InboxThreadDoc_ArchiveView; correlationId: string },
    ApiError,
    { threadId: string; trustId: string }
  >({
    mutationFn: ({ threadId, trustId }) => {
      const search = new URLSearchParams({ trustId });
      return api<{ thread: InboxThreadDoc_ArchiveView; correlationId: string }>(
        `/api/v1/inbox/threads/${encodeURIComponent(threadId)}/archive?${search.toString()}`,
        { method: 'POST' },
      );
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['inbox', 'threads', 'list'] });
      void queryClient.invalidateQueries({
        queryKey: ['inbox', 'thread', 'detail', variables.threadId],
      });
    },
  });
}

export interface AddThreadParticipantInput {
  threadId: string;
  trustId: string;
  userId: string;
  displayName: string;
  role: InboxParticipantRole;
}

/**
 * B7 — POST /v1/inbox/threads/{threadId}/participants?trustId=… (backend
 * `inbox-thread-participant-add.ts`). ADMIN-TIER only server-side
 * (`isTenantWideWriter`: admin / compliance / director / super_admin) — this
 * grants thread access, a management op, so it is NOT participant-gated. No
 * step-up. Idempotent (re-adding an existing participant is a no-op).
 * HISTORY VISIBILITY: message reads are participant-gated at THREAD level
 * with no per-message visibility — a newly added participant sees the FULL
 * prior history; confirm copy must say so.
 */
export function useAddThreadParticipant() {
  const queryClient = useQueryClient();
  return useMutation<
    { thread: InboxThreadDetail; correlationId: string },
    ApiError,
    AddThreadParticipantInput
  >({
    mutationFn: ({ threadId, trustId, userId, displayName, role }) => {
      const search = new URLSearchParams({ trustId });
      return api<{ thread: InboxThreadDetail; correlationId: string }>(
        `/api/v1/inbox/threads/${encodeURIComponent(threadId)}/participants?${search.toString()}`,
        { method: 'POST', body: { userId, displayName, role } },
      );
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['inbox', 'thread', 'detail', variables.threadId],
      });
      void queryClient.invalidateQueries({ queryKey: ['inbox', 'threads', 'list'] });
    },
  });
}

// ── end lane B ────────────────────────────────────────────────────────────────

/** Patch the caller's lastReadAt on a thread. Idempotent server-side. */
export function useMarkThreadRead() {
  const queryClient = useQueryClient();
  return useMutation<
    { thread: InboxThreadDetail; correlationId: string },
    Error,
    { threadId: string; trustId: string; asOf?: string }
  >({
    mutationFn: ({ threadId, trustId, asOf }) => {
      const search = new URLSearchParams({ trustId });
      return api<{ thread: InboxThreadDetail; correlationId: string }>(
        `/api/v1/inbox/threads/${encodeURIComponent(threadId)}/read?${search.toString()}`,
        {
          method: 'POST',
          body: asOf ? { asOf } : {},
        },
      );
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['inbox', 'threads', 'list'] });
      void queryClient.invalidateQueries({
        queryKey: ['inbox', 'thread', 'detail', variables.threadId],
      });
    },
  });
}
